import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import packagingWarehouse from '../class/packagingWarehouse';
import { formatRpcError } from '../class/jrpc20WSClient';
import { toNum, fmtQty, perColloOf, piecesWholeFrom, isPartialRow } from '../class/util';
import swal from 'sweetalert';
import PkgSelect from './component/PkgSelect';
import PositionPicker from './component/PositionPicker';
import QtyBlock from './component/QtyBlock';

// Correzione giacenza: cerca (Cliente→Divisione→Codice), seleziona una riga e rettifica
// lotto / posizione / quantità (valori assoluti). Codice e divisione NON si cambiano dal
// portale (operazione più delicata: resta in backoffice).
const CorrezionePage = () => {
    const navigate = useNavigate();
    const ws = new packagingWarehouse();

    const [customers, setCustomers] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [components, setComponents] = useState([]);
    const [positions, setPositions] = useState([]);
    const [customerUuid, setCustomerUuid] = useState('');
    const [divisionUuid, setDivisionUuid] = useState('');
    const [componentUuid, setComponentUuid] = useState('');
    const [rows, setRows] = useState(null);
    const [sel, setSel] = useState(null);          // riga selezionata + campi editabili
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([ws.getDivisionCatalog(), ws.getPositions()])
            .then(([cat, pos]) => { setCustomers(cat.customers || []); setDivisions(cat.divisions || []); setPositions(pos.positions || []); })
            .catch(err => swal('Errore', formatRpcError(err), 'error'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const divisionsForCustomer = divisions.filter(d => String(d.customer_uuid) === String(customerUuid));

    const onCustomerChange = (v) => { setCustomerUuid(v); setDivisionUuid(''); setComponentUuid(''); setComponents([]); setRows(null); setSel(null); };
    const onDivisionChange = (v) => {
        setDivisionUuid(v); setComponentUuid(''); setComponents([]); setRows(null); setSel(null);
        if (v) ws.getComponentsForDivision(v).then(r => setComponents(r.components || [])).catch(err => swal('Errore', formatRpcError(err), 'error'));
    };
    const onComponentChange = (v) => {
        setComponentUuid(v); setRows(null); setSel(null);
        const comp = components.find(c => c.uuid === v);
        if (v && comp && divisionUuid) {
            ws.getPositionsByCode(divisionUuid, comp.code).then(r => setRows(r.rows || [])).catch(err => swal('Errore', formatRpcError(err), 'error'));
        }
    };

    const selectRow = (r) => {
        setSel({
            stock_uuid: r.stock_uuid,
            position_cella: r.position_cella || '',
            lotto: r.lotto || '',
            // Il tipo di riga decide cosa è correggibile: su un collo aperto solo il residuo.
            isPartial: isPartialRow(r),
            hasParent: !!r.partial_of_uuid,
            // fmtQty (non String()): i campi si rileggono con toNum, che è un parser in formato
            // IT. Un `String(5.5)` = "5.5" verrebbe riletto come 55 (punto = migliaia), e una
            // pezzatura decimale si salverebbe moltiplicata per 10. fmtQty("5,5"/"1.650")
            // fa round-trip esatto con toNum.
            colliWhole: fmtQty(Math.round(Number(r.qty_colli_whole) || 0)),
            perColloWhole: perColloOf(r) > 0 ? fmtQty(perColloOf(r)) : '',
            piecesPartial: fmtQty(Number(r.qty_pieces_partial) || 0),
            note: '',
            // Pezzi interi memorizzati: serve solo per accorgersi delle righe non uniformi (sotto).
            storedPiecesWhole: Number(r.qty_pieces_whole) || 0,
        });
    };

    const upd = (k, v) => setSel(prev => ({ ...prev, [k]: v }));

    // Riga pre-migrazione con colli interi non uniformi: la pezzatura precompilata non ricostruisce
    // i pezzi memorizzati, e salvando la giacenza verrebbe normalizzata. Va detto PRIMA di confermare,
    // altrimenti si apre, non si tocca nulla, si salva e il totale cambia da solo.
    const nonUniformWarn = (() => {
        if (!sel || sel.isPartial) return null;
        const colli = toNum(sel.colliWhole);
        const normalized = piecesWholeFrom(sel.colliWhole, sel.perColloWhole);
        return (colli > 0 && Math.abs(sel.storedPiecesWhole - normalized) > 0.00001)
            ? `Giacenza non uniforme (${fmtQty(sel.storedPiecesWhole)} pz su ${fmtQty(colli)} colli): salvando verrà normalizzata a ${fmtQty(normalized)} pz.`
            : null;
    })();

    const handleSave = async () => {
        const position = positions.find(p => p.cella === sel.position_cella.trim());
        if (!position) { swal('Attenzione', 'Posizione non valida', 'warning'); return; }
        if (!sel.isPartial && toNum(sel.colliWhole) > 0 && toNum(sel.perColloWhole) <= 0) {
            swal('Attenzione', 'Indicare i pezzi per collo', 'warning'); return;
        }

        const confirm = await swal({
            title: 'Conferma Correzione',
            text: 'I valori inseriti SOSTITUISCONO la giacenza attuale. Procedere?',
            icon: 'warning', buttons: ['Annulla', 'Conferma'], dangerMode: true,
        });
        if (!confirm) return;

        setSaving(true);
        try {
            const payload = {
                stock_uuid: sel.stock_uuid,
                new_position_uuid: position.uuid,
                new_lotto: sel.lotto.trim(),
                note: sel.note.trim(),
            };
            // Le quantità dipendono dal TIPO di riga.
            if (sel.isPartial) {
                // Collo aperto: solo il residuo (0 = collo finito, la riga si chiude).
                payload.new_qty_pieces_partial = toNum(sel.piecesPartial);
            } else {
                payload.new_qty_colli_whole = toNum(sel.colliWhole);
                // I pezzi interi li deriva il BE: si corregge la pezzatura, non il totale.
                payload.new_qty_per_collo_whole = toNum(sel.perColloWhole);
            }
            await ws.correctStock(payload);
            await swal('Correzione salvata', 'La giacenza è stata aggiornata.', 'success');
            navigate('/home');
        } catch (err) {
            swal('Errore', formatRpcError(err), 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="pkg-page">
            <div className="pkg-page-header">
                <h2 className="pkg-title">Correzione giacenza</h2>
            </div>

            <div className="pkg-card">
                <div className="pkg-row">
                    <div className="pkg-field">
                        <label>Cliente</label>
                        <PkgSelect
                            value={customerUuid}
                            options={customers.map(c => ({ value: c.uuid, label: c.name }))}
                            onChange={onCustomerChange}
                            placeholder="Cerca cliente..."
                            noOptionsText="Nessun cliente"
                        />
                    </div>
                    <div className="pkg-field">
                        <label>Divisione</label>
                        <PkgSelect
                            value={divisionUuid}
                            options={divisionsForCustomer.map(d => ({ value: d.uuid, label: d.name }))}
                            onChange={onDivisionChange}
                            isDisabled={!customerUuid}
                            placeholder={customerUuid ? 'Cerca divisione...' : 'Seleziona prima il cliente'}
                            noOptionsText="Nessuna divisione"
                        />
                    </div>
                    <div className="pkg-field">
                        <label>Codice articolo</label>
                        <PkgSelect
                            value={componentUuid}
                            options={components.map(c => ({ value: c.uuid, label: `${c.code} - ${c.description}` }))}
                            onChange={onComponentChange}
                            isDisabled={!divisionUuid}
                            placeholder={divisionUuid ? (components.length ? 'Cerca articolo...' : 'Nessun articolo a giacenza') : 'Seleziona prima la divisione'}
                            noOptionsText="Nessun articolo"
                        />
                    </div>
                </div>
            </div>

            {rows !== null && (
                <div className="pkg-table-wrap">
                    <table>
                        <thead>
                            <tr className="table-head">
                                <th>Posizione</th><th>Lotto</th>
                                <th className="text-right">Colli int.</th><th className="text-right">Collo aperto</th><th className="text-right">Tot.</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.stock_uuid} className={sel && sel.stock_uuid === r.stock_uuid ? '' : 'order'}>
                                    {/* Un collo aperto è una riga a sé: rientrata, così si legge come figlia
                                        della riga interi invece che come un duplicato. */}
                                    <td>{isPartialRow(r) && <span className="muted">└ </span>}{r.position_cella}</td>
                                    <td>{r.lotto}</td>
                                    {/* La pezzatura è in chiave: due righe possono avere stessa cella e stesso
                                        lotto e differire SOLO per questa. Senza il "× N" sembrerebbero duplicati. */}
                                    <td className="text-right">
                                        {isPartialRow(r) ? <span className="muted">—</span> : <>
                                            {fmtQty(r.qty_colli_whole)}
                                            {perColloOf(r) > 0 && <span className="muted"> × {fmtQty(perColloOf(r))}</span>}
                                        </>}
                                    </td>
                                    <td className="text-right">
                                        {isPartialRow(r)
                                            ? <>{fmtQty(r.qty_pieces_partial)} <span className="muted">pz</span></>
                                            : <span className="muted">—</span>}
                                    </td>
                                    <td className="text-right"><strong>{fmtQty(r.qty_total)}</strong></td>
                                    <td><button className="btn-sm blue_butt" onClick={() => selectRow(r)}>Correggi</button></td>
                                </tr>
                            ))}
                            {rows.length === 0 && <tr><td colSpan="6" style={{ textAlign: 'center', padding: '20px' }} className="muted">Nessuna giacenza per questo codice.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}

            {sel && (
                <div className="pkg-card" style={{ marginTop: '1.5rem' }}>
                    <div className="pkg-section-title">Rettifica (valori assoluti)</div>
                    <div className="pkg-banner pkg-banner-warn">I valori inseriti <strong>sostituiscono</strong> la giacenza attuale (non si sommano).</div>
                    <div className="pkg-row">
                        <div className="pkg-field">
                            <label>Posizione (cella)</label>
                            <PositionPicker
                                positions={positions}
                                value={sel.position_cella}
                                onChange={v => upd('position_cella', v)}
                            />
                        </div>
                    </div>
                    <div className="pkg-row">
                        <div className="pkg-field"><label>Lotto</label><input value={sel.lotto} onChange={e => upd('lotto', e.target.value)} placeholder="(nessun lotto)" /></div>
                    </div>
                    {/* Su un collo aperto l'unica quantità è il residuo: si mostra solo quel campo. */}
                    {sel.isPartial ? (
                        <>
                            <div className="pkg-banner pkg-banner-info">
                                Riga <strong>collo aperto</strong>: si corregge solo il residuo (0 = collo finito, la riga si chiude).
                                {sel.hasParent ? ' La collocazione segue i colli interi: per spostarlo, correggi quella riga.' : ''}
                            </div>
                            <div className="pkg-row">
                                <div className="pkg-field">
                                    <label>Pezzi residui nel collo</label>
                                    <input inputMode="decimal" value={sel.piecesPartial} onChange={e => upd('piecesPartial', e.target.value)} />
                                </div>
                            </div>
                        </>
                    ) : (
                        <QtyBlock
                            title="Quantità"
                            colliWhole={sel.colliWhole}
                            perColloWhole={sel.perColloWhole}
                            piecesPartial={sel.piecesPartial}
                            onChange={upd}
                            warn={nonUniformWarn}
                            bare
                        />
                    )}
                    <div className="pkg-row">
                        <div className="pkg-field"><label>Note</label><textarea rows="2" value={sel.note} onChange={e => upd('note', e.target.value)} placeholder="Motivo della correzione" /></div>
                    </div>
                    <div className="pkg-actions-right">
                        <button className="btn-indietro" onClick={() => setSel(null)}>Annulla</button>
                        <button className="blue_butt" onClick={handleSave} disabled={saving}>{saving ? 'Salvataggio...' : 'Salva Correzione'}</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CorrezionePage;
